import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Play, Stop, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

function fmtDuration(start) {
  if (!start) return "";
  const sec = Math.max(0, Math.floor((Date.now() - new Date(start).getTime()) / 1000));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

/**
 * Kompakt snabbstämpel-kort för dashboarden.
 *
 * Regler:
 * - Om arbetsdagen är inaktiv: visa "Stämpla in arbetsdag" (1 klick).
 *   Backend väljer default-arbetsplats via user.default_workplace_id
 *   eller organisationens enda arbetsplats.
 * - Om arbetsdagen är aktiv men ingen objekt-stämpling: länka till /stamp.
 * - Om båda aktiva: visa vad de jobbar med + "Stämpla ut moment"
 *   och "Stämpla ut arbetsdag".
 */
export default function QuickStamp() {
  const [workday, setWorkday] = useState(null);
  const [activeReport, setActiveReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);

  const load = async () => {
    try {
      setLoading(true);
      const [w, a] = await Promise.all([
        api.get("/workday/current").catch(() => ({ data: null })),
        api.get("/reports/active").catch(() => ({ data: null })),
      ]);
      setWorkday(w.data || null);
      setActiveReport(a.data || null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Live-tick för duration
  useEffect(() => {
    if (!workday && !activeReport) return;
    const t = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, [workday, activeReport]);

  const stampInWorkday = async () => {
    try {
      setBusy(true);
      // Ingen workplace_id — backend faller tillbaka på default_workplace_id
      await api.post("/workday/in", {});
      toast.success("Arbetsdag startad");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte stämpla in arbetsdag");
    } finally {
      setBusy(false);
    }
  };

  const stampOutWorkday = async () => {
    try {
      setBusy(true);
      await api.post("/workday/out", {});
      toast.success("Arbetsdag avslutad");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte stämpla ut arbetsdag");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="tk-card p-5" data-testid="quick-stamp-loading">
        <div className="h-16 animate-pulse bg-[var(--brand-sand)]/40 rounded" />
      </div>
    );
  }

  // Case 1: ingen arbetsdag → snabbknapp
  if (!workday) {
    return (
      <div
        className="tk-card p-6 border-2 border-[var(--brand-forest)]/25 bg-gradient-to-br from-[var(--brand-sand)]/30 to-white"
        data-testid="quick-stamp-idle"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-widest text-text-muted">
              Snabbstämpel
            </div>
            <h2 className="font-heading text-2xl mt-1">Redo att köra igång?</h2>
            <p className="text-sm text-text-muted mt-1">
              Ett klick startar din arbetsdag. Välj moment i nästa steg.
            </p>
          </div>
          <button
            data-testid="quick-stamp-in-btn"
            onClick={stampInWorkday}
            disabled={busy}
            className="tk-btn-primary !py-3 !px-6 inline-flex items-center gap-2 text-base disabled:opacity-60"
          >
            <Play size={18} weight="fill" />
            {busy ? "Stämplar in…" : "Stämpla in arbetsdag"}
          </button>
        </div>
      </div>
    );
  }

  // Case 2: workday aktiv men inget moment
  if (!activeReport) {
    void tick;
    return (
      <div
        className="tk-card p-6 border-2 border-[var(--brand-forest)]/30 bg-gradient-to-br from-white to-[var(--brand-sand)]/30"
        data-testid="quick-stamp-workday-only"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-widest text-text-muted">
              Arbetsdag aktiv
            </div>
            <h2 className="font-heading text-2xl mt-1">
              <Clock size={20} weight="bold" className="inline mr-1 -mt-1 text-[var(--brand-forest)]" />
              {fmtDuration(workday.stamp_in)}
            </h2>
            <p className="text-sm text-text-muted mt-1">
              {workday.workplace_name
                ? `Instämplad på ${workday.workplace_name}`
                : "Ingen arbetsplats vald"}
              {" — "}välj ett moment för att börja fakturera tid.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link
              to="/stamp"
              data-testid="quick-stamp-pick-task"
              className="tk-btn-primary !py-2.5 !px-5 inline-flex items-center gap-2"
            >
              Välj moment <ArrowRight size={16} weight="bold" />
            </Link>
            <button
              data-testid="quick-stamp-out-workday"
              onClick={stampOutWorkday}
              disabled={busy}
              className="tk-btn-outline !py-2.5 !px-5 inline-flex items-center gap-2"
            >
              <Stop size={16} weight="fill" /> Avsluta dag
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Case 3: allt aktivt
  void tick;
  return (
    <div
      className="tk-card p-6 border-2 border-[var(--brand-rust)]/25 bg-gradient-to-br from-white to-[var(--brand-rust)]/5"
      data-testid="quick-stamp-active"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-widest text-text-muted flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[var(--brand-rust)] animate-pulse" />
            Instämplad nu
          </div>
          <h2 className="font-heading text-2xl mt-1 truncate">
            {activeReport.object_name}
          </h2>
          <p className="text-sm text-text-muted mt-0.5">
            {activeReport.task_name} · {fmtDuration(activeReport.start_time)}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link
            to="/stamp"
            data-testid="quick-stamp-manage"
            className="tk-btn-primary !py-2.5 !px-5 inline-flex items-center gap-2"
          >
            Hantera <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </div>
    </div>
  );
}
